import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MeetingsIBooked from "./MeetingsIBooked";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog";
import { importanceMeta } from "@/lib/taskUi";
import { ChevronLeft, ChevronRight, Flag, CalendarClock as CalIcon, ExternalLink, Clock, Share2, Copy, Pencil, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useUserTz, fmtTimeTz, dateKeyTz, timeHHMMTz, zonedToUtcIso } from "@/lib/tz";

function TzBadge() {
  const tz = useUserTz();
  return (
    <p className="text-sm text-slate-600 mb-3" data-testid="cal-timezone">Showing times in <b className="text-slate-800">{tz.label || tz.timezone}</b>
      {tz.read_only ? <span className="text-xs text-slate-400"> · the viewed user's setting</span> : <a href="/profile?tab=work#timezone" className="ml-2 text-[#006090] underline text-xs" data-testid="cal-change-tz">Change Time Zone</a>}</p>
  );
}

const MODES = ["Month", "Week", "Day"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function Calendar() {
  const { canRecruiting, isOutside, canSchedule: canScheduleCap } = useAuth();
  const canSchedule = canScheduleCap && !isOutside;
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [scope, setScope] = useState(sp.get("tab") === "booked" ? "booked" : "my");
  const [mode, setMode] = useState("Month");
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ member_id: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [appt, setAppt] = useState(null);
  const [outlookOn, setOutlookOn] = useState(false);

  const range = useCallback(() => {
    if (mode === "Day") return [iso(cursor), iso(cursor)];
    if (mode === "Week") { const s = addDays(cursor, -cursor.getDay()); return [iso(s), iso(addDays(s, 6))]; }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = addDays(first, -first.getDay());
    return [iso(start), iso(addDays(start, 41))];
  }, [cursor, mode]);

  const load = useCallback(async () => {
    const [start, end] = range();
    try {
      if (scope === "team") {
        const params = { start, end, ...filters };
        Object.keys(params).forEach((k) => !params[k] && delete params[k]);
        const { data } = await api.get("/calendar/team", { params });
        setItems(data.items); setMembers(data.members || []); setOutlookOn(false);
      } else {
        const { data } = await api.get("/calendar/my", { params: { start, end } });
        let merged = data.items.map((it) => (it.type === "appointment" && it.due_date ? { ...it, start_raw: it.due_date, due_date: dateKeyTz(it.due_date) } : it));
        try {
          const ok = await api.get("/calendar/outlook", { params: { start, end } });
          if (ok.data.available) {
            setOutlookOn(true);
            merged = [...merged, ...ok.data.items.map((e) => ({ ...e, due_date: dateKeyTz(e.start) }))];
          } else setOutlookOn(false);
        } catch { setOutlookOn(false); }
        try {
          const { data: regs } = await api.get("/events/my-registrations");
          const evs = [...regs.upcoming, ...regs.waitlisted, ...regs.past].filter((ev) => ev.status !== "cancelled");
          merged = [...merged, ...evs.map((ev) => ({ id: `training-${ev.id}`, event_id: ev.id, type: "training", title: ev.title,
            waitlisted: ev.my_registration?.status === "waitlisted", start_utc: ev.start_utc, due_date: dateKeyTz(ev.start_utc) }))];
        } catch { /* training overlay is optional */ }
        setItems(merged);
      }
    } catch (e) {
      // Calendar is a universal workspace page; some roles (e.g. Outside Recruiters) can't hit
      // recruiting-scoped calendar calls. Don't surface a permission error toast — the page still
      // renders the user's own events.
      if (e?.response?.status !== 403) toast.error(apiError(e));
    }
  }, [range, scope, filters]);
  useEffect(() => { load(); }, [load]);

  const move = (n) => setCursor((c) => mode === "Month" ? new Date(c.getFullYear(), c.getMonth() + n, 1) : addDays(c, n * (mode === "Week" ? 7 : 1)));
  const itemsFor = (dstr) => items.filter((it) => (it.due_date || "").slice(0, 10) === dstr);
  const openCreate = (dstr) => { setAddDate(dstr); setAddOpen(true); };
  const onDropTask = async (e, dstr) => {
    const tid = e.dataTransfer.getData("text/task");
    if (!tid) return;
    try { await api.patch(`/tasks/${tid}`, { due_date: dstr }); toast.success("Task rescheduled"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const title = mode === "Month" ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : mode === "Day" ? cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : `Week of ${addDays(cursor, -cursor.getDay()).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <div>
      <PageHeader title="Calendar" description="Your tasks and milestones over time."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {canSchedule && <Button size="sm" onClick={() => navigate("/calendar/scheduling-pages")} data-testid="cal-scheduling-pages" className="bg-[#006090] hover:bg-[#004E75] text-white"><CalIcon className="w-4 h-4 mr-1.5" />Scheduling Pages</Button>}
            {canSchedule && <ShareScheduleButton navigate={navigate} />}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={() => setScope("my")} data-testid="cal-scope-my" className={`px-3 py-2 text-sm ${scope === "my" ? "bg-brand text-white" : "bg-white text-slate-600"}`}>My Calendar</button>
              <button onClick={() => setScope("team")} data-testid="cal-scope-team" className={`px-3 py-2 text-sm ${scope === "team" ? "bg-brand text-white" : "bg-white text-slate-600"}`}>Team Calendar</button>
              {canRecruiting && <button onClick={() => setScope("booked")} data-testid="cal-scope-booked" className={`px-3 py-2 text-sm ${scope === "booked" ? "bg-brand text-white" : "bg-white text-slate-600"}`}>Meetings I Booked</button>}
            </div>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-28 h-9" data-testid="cal-mode"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <h3 className="font-display font-semibold text-slate-800 ml-2" data-testid="cal-title">{title}</h3>
        </div>
        {scope === "team" && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500" data-testid="cal-team-hint">Office calendar · only items marked “Show on Team Calendar”</span>
            <CalFilter value={filters.member_id} onChange={(v) => setFilters({ ...filters, member_id: v })} placeholder="Everyone" options={members.map((m) => [m.user_id, m.name])} testId="cal-filter-member" />
          </div>
        )}
      </div>
      {scope === "team" && members.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-slate-600" data-testid="cal-team-legend">
          {members.map((m) => <span key={m.user_id} className="flex items-center gap-1.5" data-testid={`cal-team-legend-${m.user_id}`}><span className="w-2.5 h-2.5 rounded-sm" style={{ background: teamColor(m.color_index) }} />{m.name}</span>)}
        </div>
      )}

      {scope === "my" && <TzBadge />}
      {scope === "my" && (
        <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-slate-500" data-testid="cal-legend">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#0D9488" }} />Top-Recruit interview</span>
          <span className="flex items-center gap-1.5" data-testid="cal-legend-training"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#7C3AED" }} />My training</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-dashed" style={{ background: "#2563EB12", borderColor: "#2563EB55" }} />Outlook event</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-dashed" style={{ background: "#64748b12", borderColor: "#64748b55" }} />Busy / private</span>
          {outlookOn ? <span className="text-emerald-600">· Outlook synced</span>
            : <span className="text-slate-400">· Connect Microsoft 365 in My Profile to see your Outlook events</span>}
        </div>
      )}

      {scope === "booked" ? <MeetingsIBooked /> : <>
      {mode === "Month" && <MonthGrid cursor={cursor} itemsFor={itemsFor} navigate={navigate} openCreate={openCreate} onDropTask={onDropTask} scope={scope} onAppt={setAppt} />}
      {mode === "Week" && <ListRange days={7} start={addDays(cursor, -cursor.getDay())} itemsFor={itemsFor} navigate={navigate} openCreate={openCreate} onDropTask={onDropTask} scope={scope} onAppt={setAppt} />}
      {mode === "Day" && <ListRange days={1} start={cursor} itemsFor={itemsFor} navigate={navigate} openCreate={openCreate} onDropTask={onDropTask} scope={scope} onAppt={setAppt} />}
      </>}

      <TaskFormDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} defaults={{ due_date: addDate }} title="New Task" />
      {appt && <AppointmentDialog appt={appt} navigate={navigate} onClose={() => setAppt(null)} onSaved={() => { setAppt(null); load(); }} />}
    </div>
  );
}

const TEAM_COLORS = ["#006090", "#C2410C", "#047857", "#7C3AED", "#BE123C", "#A16207", "#DB2777", "#475569", "#4D7C0F", "#78350F"];
export const teamColor = (i) => TEAM_COLORS[(i || 0) % TEAM_COLORS.length];

function EventChip({ it, navigate, scope, onAppt }) {
  const im = importanceMeta(it.importance);
  const isMs = it.type === "milestone";
  const isAppt = it.type === "appointment";
  if (it.type === "outlook") {
    const color = it.private ? "#64748b" : "#2563EB";
    return (
      <div data-testid={`cal-outlook-${it.id}`} title={it.private ? "Busy (private Outlook event)" : `Outlook: ${it.title}`}
        className="w-full truncate rounded px-1.5 py-0.5 text-[11px] mb-0.5 flex items-center gap-1 border border-dashed"
        style={{ background: `${color}12`, color, borderColor: `${color}55` }}>
        <Clock className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{it.private ? "Busy" : `${it.all_day ? "" : `${fmtTimeTz(it.start)} `}${it.title}`}</span>
      </div>
    );
  }
  if (it.type === "training") {
    const time = fmtTimeTz(it.start_utc);
    return (
      <button onClick={() => navigate(`/events/${it.event_id}`)} data-testid={`cal-training-${it.event_id}`}
        title={`${it.waitlisted ? "Waitlisted" : "Registered"} training · ${time}`}
        className="w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] mb-0.5 flex items-center gap-1"
        style={{ background: "#7C3AED14", color: "#6D28D9" }}>
        <GraduationCap className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{time} {it.title}{it.waitlisted ? " (waitlist)" : ""}</span>
      </button>
    );
  }
  if (it.type === "team") {
    const time = it.due_time ? new Date(`2000-01-01T${it.due_time}`).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
    const col = teamColor(it.color_index);
    return (
      <button onClick={() => it.is_mine && navigate(`/tasks/detail/${it.id}`)} data-testid={`cal-team-${it.id}`} title={`${it.title}${it.assignee_name ? ` · ${it.assignee_name}` : ""}`}
        className="w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] mb-0.5 flex items-center gap-1 border-l-[3px]" style={{ background: `${col}17`, color: col, borderLeftColor: col }}>
        <Clock className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{time} {it.title}{it.assignee_name ? ` · ${it.assignee_name.split(" ")[0]}` : ""}</span>
      </button>
    );
  }
  if (isAppt) {
    return (
      <button onClick={() => onAppt(it)} data-testid={`cal-item-${it.id}`}
        className="w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] mb-0.5 flex items-center gap-1"
        style={{ background: "#0D948814", color: "#0D9488" }}>
        <CalIcon className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{it.start_raw ? `${fmtTimeTz(it.start_raw)} ` : ""}{it.title}{it.person_name ? ` · ${it.person_name.split(" ")[0]}` : ""}</span>
      </button>
    );
  }
  return (
    <button draggable={!isMs} onDragStart={(e) => e.dataTransfer.setData("text/task", it.id)}
      onClick={() => !isMs && navigate(`/tasks/detail/${it.id}`)} data-testid={`cal-item-${it.id}`}
      className="w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] mb-0.5 flex items-center gap-1"
      style={{ background: isMs ? "#EEF2FF" : `${im.color}1A`, color: isMs ? "#4338CA" : im.color }}>
      {isMs ? <Flag className="w-2.5 h-2.5 shrink-0" /> : <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: im.color }} />}
      <span className="truncate">{it.title}{scope === "team" && it.assignee_name ? ` · ${it.assignee_name.split(" ")[0]}` : ""}</span>
    </button>
  );
}

function MonthGrid({ cursor, itemsFor, navigate, openCreate, onDropTask, scope, onAppt }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = addDays(first, -first.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const todayStr = iso(new Date());
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="calendar-month">
      <div className="grid grid-cols-7 border-b border-slate-100">{DOW.map((d) => <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-400 text-center">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const dstr = iso(d); const inMonth = d.getMonth() === cursor.getMonth();
          const evs = itemsFor(dstr);
          return (
            <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropTask(e, dstr)}
              onClick={() => openCreate(dstr)} data-testid={`cal-day-${dstr}`}
              className={`min-h-[92px] border-b border-r border-slate-100 p-1.5 cursor-pointer hover:bg-slate-50 ${inMonth ? "" : "bg-slate-50/50"}`}>
              <span className={`text-xs ${dstr === todayStr ? "bg-brand text-white rounded-full w-5 h-5 inline-flex items-center justify-center" : inMonth ? "text-slate-600" : "text-slate-300"}`}>{d.getDate()}</span>
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                {evs.slice(0, 3).map((it) => <EventChip key={it.id} it={it} navigate={navigate} scope={scope} onAppt={onAppt} />)}
                {evs.length > 3 && <span className="text-[10px] text-slate-400">+{evs.length - 3} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListRange({ days, start, itemsFor, navigate, openCreate, onDropTask, scope, onAppt }) {
  const cells = Array.from({ length: days }, (_, i) => addDays(start, i));
  return (
    <div className="space-y-2" data-testid="calendar-list">
      {cells.map((d) => {
        const dstr = iso(d); const evs = itemsFor(dstr);
        return (
          <div key={dstr} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropTask(e, dstr)} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`cal-day-${dstr}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-700">{d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
              <Button size="sm" variant="ghost" onClick={() => openCreate(dstr)} data-testid={`cal-add-${dstr}`}>+ Add</Button>
            </div>
            {evs.length ? <div className="space-y-1">{evs.map((it) => <EventChip key={it.id} it={it} navigate={navigate} scope={scope} onAppt={onAppt} />)}</div>
              : <p className="text-xs text-slate-300">No tasks</p>}
          </div>
        );
      })}
    </div>
  );
}

function ShareScheduleButton({ navigate }) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState(null);
  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Scheduling link copied"); };
  const onClick = async () => {
    try {
      const { data } = await api.get("/scheduling-pages/share-info");
      const items = data.items || [];
      if (items.length === 0) { toast.message("No active scheduling page yet.", { description: "Create one under Scheduling Pages.", action: { label: "Create", onClick: () => navigate("/calendar/scheduling-pages") } }); return; }
      if (items.length === 1) { copy(items[0].public_url); return; }
      setPages(items); setOpen(true);
    } catch { toast.error("Could not load your scheduling pages."); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={onClick} data-testid="cal-share-schedule"><Share2 className="w-4 h-4 mr-1.5" />Share Scheduling Link</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="share-schedule-dialog">
          <DialogHeader><DialogTitle>Choose a scheduling page</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(pages || []).map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-200 p-3" data-testid={`share-page-${p.id}`}>
                <p className="font-medium text-slate-800 text-sm">{p.name}</p>
                <p className="text-xs text-slate-400 truncate">{p.public_url}</p>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copy(p.public_url)} data-testid={`share-copy-${p.id}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy Link</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(p.public_url, "_blank")} data-testid={`share-preview-${p.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Preview</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/calendar/scheduling-pages/${p.id}`)} data-testid={`share-edit-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CalFilter({ value, onChange, placeholder, options, testId }) {
  const NONE = "__none__";
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
      <SelectTrigger className="h-9 w-40 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white max-h-64"><SelectItem value={NONE}>{placeholder}</SelectItem>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function AppointmentDialog({ appt, navigate, onClose, onSaved }) {
  const raw = appt.start_raw;
  const [date, setDate] = useState(raw ? dateKeyTz(raw) : (appt.due_date || "").slice(0, 10));
  const [time, setTime] = useState(raw ? timeHHMMTz(raw) : "10:00");
  const tz = useUserTz();
  const reschedule = async () => {
    try { await api.patch(`/appointments/${appt.id}`, { start: zonedToUtcIso(date, time, tz.timezone) }); toast.success("Interview rescheduled"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const complete = async () => { try { await api.patch(`/appointments/${appt.id}`, { status: "completed" }); toast.success("Marked complete"); onSaved(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="cal-appt-dialog">
        <DialogHeader><DialogTitle>{appt.title || "Interview"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {appt.person_name && <p className="text-slate-600">Candidate: <b>{appt.person_name}</b></p>}
          {appt.meeting_link && <a href={appt.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand font-medium" data-testid="cal-appt-join"><ExternalLink className="w-4 h-4" />Join meeting</a>}
          {appt.opportunity_id && <button className="block text-brand text-xs" onClick={() => navigate(`/recruiting/opportunity/${appt.opportunity_id}`)} data-testid="cal-appt-open-opp">Open candidate record</button>}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><label className="text-xs font-semibold text-slate-600">Date</label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cal-appt-date" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Time ({tz.timezone})</label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cal-appt-time" /></div>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {appt.status !== "completed" && <Button variant="outline" onClick={complete} data-testid="cal-appt-complete">Mark Complete</Button>}
          <Button onClick={reschedule} className="bg-brand hover:bg-brand-strong" data-testid="cal-appt-reschedule">Reschedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
